import React, { useCallback, useEffect, useMemo, useRef } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import { Typography, Box, Skeleton } from "@mui/material"
import "@syncfusion/ej2/material.css"
import {
  AccumulationChartComponent,
  Inject,
  AccumulationLegend,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
} from "@syncfusion/ej2-react-charts"
import OverviewCard, { OVERVIEW_CHART_CARD_MIN_HEIGHT_PX } from "./OverviewCard"
import { OVERVIEW_CHART_FOOTER_MIN_HEIGHT_PX } from "./constants/overviewGrid"
import { formatChartNumber } from "./utils/numberFormat"

const toChartY = value => {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n : 0
}

const DonutChartCard = ({
  dataSource,
  title,
  infoText,
  isLoading,
  currency,
}) => {
  const containerRef = useRef(null)
  const chartRef = useRef(null)

  const requestChartRefresh = useCallback(() => {
    chartRef.current?.refresh?.()
  }, [])

  const list = Array.isArray(dataSource) ? dataSource : []
  const hasPositiveSlice = list.some(p => toChartY(p?.y) > 0)
  const isEmptyDisplay = !hasPositiveSlice

  const chartData = useMemo(() => {
    if (list.length > 0) {
      return list
    }
    return [
      {
        x: i18n.t("overview.donutEmptyStateLabel"),
        y: 1,
        text: i18n.t("overview.donutEmptyStateLabel"),
        fill: "#D1D5DB",
      },
    ]
  }, [list])

  const legendValueByLabel = useMemo(() => {
    const map = new Map()
    list.forEach(point => {
      const label = point?.x
      if (label == null || label === "") return
      const amount = `${formatChartNumber(toChartY(point?.y))}${
        currency ? ` ${currency}` : ""
      }`.trim()
      map.set(label, amount)
    })
    return map
  }, [list, currency])

  useEffect(() => {
    if (isLoading) return
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(requestChartRefresh)
    })
    const el = containerRef.current
    if (el) ro.observe(el)
    return () => ro.disconnect()
  }, [isLoading, requestChartRefresh, dataSource, isEmptyDisplay])

  const handleLegendRender = args => {
    if (!args?.text) return
    const amount = legendValueByLabel.get(args.text)
    if (amount == null) return
    args.text = `${args.text}: ${amount}`
  }

  return (
    <OverviewCard
      title={title}
      infoText={infoText}
      loading={isLoading}
      sx={{ minHeight: OVERVIEW_CHART_CARD_MIN_HEIGHT_PX }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        {isLoading ? (
          <Skeleton variant="rectangular" width="100%" height={300} />
        ) : (
          <AccumulationChartComponent
            ref={chartRef}
            width="100%"
            height="300px"
            background="white"
            enableResize={true}
            tooltip={{ enable: false }}
            legendRender={handleLegendRender}
            legendSettings={{
              visible: list.length > 0,
            }}
          >
            <Inject services={[AccumulationLegend]} />
            <AccumulationSeriesCollectionDirective>
              <AccumulationSeriesDirective
                dataSource={chartData}
                xName="x"
                yName="y"
                pointColorMapping="fill"
                type="Pie"
                innerRadius="60%"
              />
            </AccumulationSeriesCollectionDirective>
          </AccumulationChartComponent>
        )}
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          minHeight: OVERVIEW_CHART_FOOTER_MIN_HEIGHT_PX,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 0.5,
        }}
      ></Box>
    </OverviewCard>
  )
}

export default DonutChartCard

DonutChartCard.propTypes = {
  dataSource: PropTypes.arrayOf(
    PropTypes.shape({
      x: PropTypes.string.isRequired,
      y: PropTypes.number,
      text: PropTypes.string,
      fill: PropTypes.string,
    }),
  ),
  title: PropTypes.string.isRequired,
  infoText: PropTypes.string,
  isLoading: PropTypes.bool,
  currency: PropTypes.string,
}

DonutChartCard.defaultProps = {
  dataSource: [],
}
